import React, { useState } from "react";
import {
  Bars3Icon,
  CogIcon,
  UserIcon,
  BellIcon,
  LockClosedIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import Sidebar from "./sidebar";
import logoes from "../assets/Logo.png";

const Settings = () => {
  const { t, i18n } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: false,
  });

  // Handle profile data change
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle notification toggle
  const handleNotificationToggle = (type) => {
    setNotifications((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Handle language change
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    i18n.changeLanguage(newLanguage);
    localStorage.setItem("language", newLanguage);
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      // Add your profile update API call here
      console.log("Profile updated:", profileData);
      // Show success message
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  // Handle password update
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert(t("settings.passwordMismatch"));
      return;
    }
    try {
      // Add your password update API call here
      console.log("Password updated");
      setPasswordData({ newPassword: "", confirmPassword: "" });
      // Show success message
    } catch (error) {
      console.error("Error updating password:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        language={i18n.language}
        logo={logoes}
        translations={{
          en: {
            home: t("home"),
            myprofile: t("myProfile"),
            points: t("points.pointstitle"),
            favourites: t("favourites"),
            matchingProfiles: t("matchingProfiles"),
            logout: t("logout"),
          },
          ta: {
            home: t("home"),
            myprofile: t("myProfile"),
            points: t("points"),
            favourites: t("favourites"),
            matchingProfiles: t("matchingProfiles"),
            logout: t("logout"),
          },
        }}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Dashboard Header */}
        <div className="bg-white p-4 lg:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              {/* Bars3Icon for mobile view */}
              <Bars3Icon
                className="h-8 w-8 mr-2 text-purple-600 cursor-pointer lg:hidden"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              />
              <CogIcon className="h-8 w-8 text-purple-600 hidden lg:block" />
              {/* Settings Text */}
              <span className="ml-2">{t("settings.settingstitle")}</span>
            </h1>
          </div>
        </div>

        {/* Settings Content */}
        <div className="p-4 lg:p-8 max-w-4xl mx-auto">
          <div className="space-y-6">
            {/* Profile Settings */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold text-gray-700 flex items-center">
                <UserIcon className="h-6 w-6 mr-2 text-blue-500" />
                {t("settings.profileSettings")}
              </h2>
              <form onSubmit={handleProfileUpdate} className="mt-4 space-y-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium text-gray-600">
                    {t("settings.name")}
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t("settings.enterName")}
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium text-gray-600">
                    {t("settings.email")}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t("settings.enterEmail")}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 transition duration-200"
                >
                  {t("settings.updateProfile")}
                </button>
              </form>
            </div>

            {/* Notification Settings */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold text-gray-700 flex items-center">
                <BellIcon className="h-6 w-6 mr-2 text-yellow-500" />
                {t("settings.notificationSettings")}
              </h2>
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    {t("settings.emailNotifications")}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.emailNotifications}
                      onChange={() =>
                        handleNotificationToggle("emailNotifications")
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">
                    {t("settings.pushNotifications")}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.pushNotifications}
                      onChange={() =>
                        handleNotificationToggle("pushNotifications")
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold text-gray-700 flex items-center">
                <LockClosedIcon className="h-6 w-6 mr-2 text-red-500" />
                {t("settings.securitySettings")}
              </h2>
              <form onSubmit={handlePasswordUpdate} className="mt-4 space-y-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium text-gray-600">
                    {t("settings.newPassword")}
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={t("settings.enterNewPassword")}
                  />
                </div>
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium text-gray-600">
                    {t("settings.confirmPassword")}
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder={t("settings.confirmPasswordPlaceholder")}
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transition duration-200"
                >
                  {t("settings.updatePassword")}
                </button>
              </form>
            </div>

            {/* Language Settings */}
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h2 className="text-xl font-semibold text-gray-700 flex items-center">
                <GlobeAltIcon className="h-6 w-6 mr-2 text-green-500" />
                {t("settings.languageSettings")}
              </h2>
              <div className="mt-4 space-y-4">
                <div className="flex flex-col space-y-2">
                  <label className="text-sm font-medium text-gray-600">
                    {t("settings.selectLanguage")}
                  </label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    value={i18n.language}
                    onChange={handleLanguageChange}
                  >
                    <option value="en">{t("languages.english")}</option>
                    <option value="ta">{t("languages.tamil")}</option>
                  </select>
                </div>
                <div className="text-sm text-gray-500">
                  {t("settings.currentLanguage")}:{" "}
                  {t(`languages.${i18n.language}`)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
